import React, { useState, useEffect } from 'react';
import JanelaModal from '../../JanelaModal';
import { fetchEmbryosDaDoadora, salvarNumeracao, numerosRepetidos, renumeraDaDoadora, numeravel } from '../../../data/embryos';
import { FIV_STAGE_FIELDS } from '../../../constants/workflowGranular';

/**
 * Numeração dos embriões da FIV.
 *
 * Os embriões nascem do lançamento por contagem — "3 BI, 2 BL" —, e é aqui que
 * eles viram linhas individuais com número próprio. O número é o que a equipe
 * usa para achar a palheta depois; a fase fica editável ao lado porque quem
 * numera é quem está olhando o embrião no microscópio, e é a hora natural de
 * corrigir uma classificação.
 *
 * Ao salvar, as contagens do doador são recalculadas a partir dos embriões
 * (no RPC). Sem isso, a próxima gravação da FIV recriaria a distribuição
 * antiga por cima.
 */
const NumerarEmbrioesModal = ({ show, serviceId, donor, onClose, onSaved }) => {
  const [embrioes, setEmbrioes] = useState([]);
  // Os das OUTRAS doadoras. O número é único no SERVIÇO, não na doadora: sem
  // saber o que as vizinhas ocupam, renumerar esta escrevia por cima delas.
  const [deOutrasDoadoras, setDeOutrasDoadoras] = useState([]);
  // Os desta doadora que nao se numera: congelados e descartados.
  const [congelados, setCongelados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!show || !serviceId) return;

    let cancelado = false;
    setCarregando(true);
    setErro(null);
    // Todos os embriões desta doadora. Travar na transferência impedia
    // numerar ANTES de transferir, que é justamente quando o número é preciso.
    fetchEmbryosDaDoadora(serviceId)
      .then(lista => {
        if (cancelado) return;
        const todos = lista || [];
        const daDoadora = todos.filter(e => e.fiv_donor_id === donor?.id);

        // Só os que ainda vão para a TE entram na numeração — a mesma regra
        // que `reconcilia_embrioes_do_doador` aplica ao criar, pela função
        // `embriao_vai_a_campo`. Congelado e descarte nascem sem número.
        //
        // Eles seguem entrando na checagem de repetido porque o banco AINDA
        // pode ter número neles: um embrião que chegou de fora traz o número
        // da etiqueta de quem produziu. Ignorá-lo faria a gravação morrer no
        // índice único.
        const numeraveis = daDoadora.filter(numeravel);
        const congeladosDela = daDoadora.filter(e => !numeravel(e));
        const deOutras = todos.filter(e => e.fiv_donor_id !== donor?.id);

        // O embrião nasce sem número (a bancada não envasa na ordem da tela).
        // Doadora ainda toda em branco abre com o bloco proposto, continuando
        // do maior número do serviço: a ordem em que se abre cada doadora é a
        // ordem do envase. Só proposto — nada vai ao banco sem "Salvar".
        const emBranco = numeraveis.length > 0
          && numeraveis.every(e => e.service_number === null || e.service_number === undefined);
        setEmbrioes(emBranco
          ? renumeraDaDoadora(numeraveis, [...numeraveis, ...congeladosDela, ...deOutras])
          : numeraveis);
        setCongelados(congeladosDela);
        setDeOutrasDoadoras(deOutras);
      })
      .catch(e => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setCarregando(false); });

    return () => { cancelado = true; };
  }, [show, serviceId, donor?.id]);

  if (!show) return null;

  const alterar = (id, campo, valor) => {
    setEmbrioes(anteriores => anteriores.map(e =>
      e.id === id ? { ...e, [campo]: valor } : e));
  };

  // Contra o serviço inteiro. Checar só a doadora deixava passar o número que
  // já era da vizinha, e o erro só aparecia na gravação, como falha de índice.
  const ocupados = [...congelados, ...deOutrasDoadoras];
  const repetidos = numerosRepetidos([...embrioes, ...ocupados]);

  /**
   * Devolve esta doadora ao padrão: bloco consecutivo, BE → BI.
   *
   * O bloco entra onde ela já está na sequência do serviço — depois da doadora
   * anterior —, e as vizinhas não são tocadas. Antes isto numerava 1..X sempre,
   * o que só estava certo para a primeira doadora: nas outras escrevia por
   * cima dos números da primeira, e a gravação morria no índice único.
   *
   * A lista reordena junto: o que se vê passa a ser a ordem dos números.
   */
  const renumerar = () => {
    setEmbrioes(anteriores =>
      renumeraDaDoadora(anteriores, [...anteriores, ...ocupados]));
  };

  const salvar = async () => {
    if (repetidos.length > 0) return;

    setSalvando(true);
    setErro(null);
    try {
      await salvarNumeracao(serviceId, embrioes);
      onSaved?.();
      onClose();
    } catch (e) {
      console.error('Erro ao salvar numeração:', e);
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <JanelaModal chave="numerar-embrioes" camada="z-[60]" largura="sm:max-w-2xl"
      titulo={`Numerar embriões${donor?.donor_identifier ? ` — ${donor.donor_identifier}` : ''}`}
      subtitulo={<>
        {donor?.bull ? `× ${donor.bull} · ` : ''}
        Um número por embrião. A fase pode ser corrigida aqui.
      </>}
      onFechar={onClose}>
        <div className="flex-1 overflow-y-auto p-4">
          {carregando && <p className="text-sm text-slate-500 py-6 text-center">Carregando...</p>}

          {erro && (
            <div className="mb-3 text-sm text-slate-700 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2">
              {erro}
            </div>
          )}

          {!carregando && embrioes.length === 0 && congelados.length === 0 && (
            <p className="text-sm text-slate-500 py-6 text-center">
              Esta doadora ainda não tem embrião lançado. Preencha os estágios na linha dela.
            </p>
          )}

          {/* Dito, e não escondido. A doadora produziu mais embriões do que os
              que aparecem aqui, e sumir com a diferença faria a lista parecer
              incompleta — que foi exatamente a confusão que o congelado
              invisível já causou uma vez. */}
          {!carregando && congelados.length > 0 && (
            <p className="mb-3 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              {congelados.length === 1
                ? '1 embrião congelado desta doadora não aparece aqui'
                : `${congelados.length} embriões congelados desta doadora não aparecem aqui`}
              {' — '}a numeração é dos que vão a campo. A palheta que ficou no botijão
              se acha pelo botijão e pela caneca, no Estoque.
              {embrioes.length === 0 && ' Não há nada a numerar nesta doadora.'}
            </p>
          )}

          {embrioes.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2 pr-3 font-semibold">Doadora × Touro</th>
                  <th className="py-2 pr-3 font-semibold w-24">Estágio</th>
                  <th className="py-2 font-semibold w-28">Número</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {embrioes.map(e => (
                  <tr key={e.id}>
                    <td className="py-1.5 pr-3">
                      <span className="text-slate-800">{e.donor_identifier || donor?.donor_identifier || '—'}</span>
                      <span className="text-slate-400"> × {e.bull_name || donor?.bull || '—'}</span>
                      {/* Informa, não impede: saber que já saiu ajuda a
                          conferir, e travar a edição era o que atrapalhava. */}
                      {e.usado_service_code && (
                        <span className="ml-2 text-xs text-slate-400">
                          TE {e.usado_service_code}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3">
                      <select
                        className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                        value={e.stage || ''}
                        onChange={(ev) => alterar(e.id, 'stage', ev.target.value)}
                      >
                        <option value="">—</option>
                        {FIV_STAGE_FIELDS.map(f => (
                          <option key={f.stage} value={f.stage}>{f.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5">
                      <input
                        type="number" min="1"
                        className="w-full px-2 py-1 border border-slate-200 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-50"
                        value={e.service_number ?? ''}
                        onChange={(ev) => alterar(e.id, 'service_number',
                          ev.target.value === '' ? null : parseInt(ev.target.value))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {repetidos.length > 0 && (
            <p className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2">
              Número repetido: <strong>{repetidos.join(', ')}</strong>. É por ele que se acha
              a palheta, então dois embriões não podem dividir o mesmo.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 px-4 pb-2">
          <button
            type="button"
            onClick={renumerar}
            disabled={embrioes.length === 0}
            className="text-sm text-blue-600 hover:text-blue-800 underline disabled:opacity-40"
          >
            Renumerar esta doadora
          </button>
          <span className="text-xs text-slate-500">
            Bloco consecutivo, do BE ao BI. Não mexe nas outras doadoras.
          </span>
        </div>

        <div className="flex gap-2 p-4 border-t">
          <button
            onClick={salvar}
            disabled={salvando || carregando || embrioes.length === 0 || repetidos.length > 0}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {salvando ? 'Salvando...' : 'Salvar numeração'}
          </button>
          <button
            onClick={onClose}
            disabled={salvando}
            className="px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
    </JanelaModal>
  );
};

export default NumerarEmbrioesModal;
